<template>
  <div>
    <LoginScreen v-if="!isLoggedIn" @login="handleLogin" :companyInformation="companyInformation" />
    <div v-else class="app" :style="appStyle">
      <!--img v-if="logo" :src="logo" alt="Company logo" />
      <p v-if="loading">Loading...</p>
      <p v-if="error">{{ error }}</p-->
      <!-- <p v-if="conversations">{{ conversations }}</p> -->
      <AppSidebar :conversations="conversations" :conversation-messages-map="conversationMessagesMap" :companyInformation="companyInformation" :currentUser="currentUser" @load-conversation="loadConversation" @new-chat="resetToNewChat"
        @clear-conversation="clearConversation" @refresh-conversations="loadConversations" @logout="handleLogout"
        @collapsed="handleSidebarCollapsed" @sidebar-width="handleSidebarWidth" @update-logo="updateLogo" @show-memories="showMemoryScreen"
        @show-knowledge="showKnowledgeScreen"
        @new-chat-in-project="startNewChatInProject"
        @show-project-view="showProjectView"
        @project-created="showProjectView"
        @show-project-artifacts="showProjectArtifacts"/>
      <div :class="['main-content', { 'main-content--project': currentProject || showMemories || showKnowledge }]">
        <!-- Memory Management Screen -->
        <MemoryManagement v-if="showMemories" :companyInformation="companyInformation" @close="showMemories = false" />
        <!-- Knowledge Management Screen (Admin) -->
        <KnowledgeManagement v-else-if="showKnowledge" @close="showKnowledge = false" />
        <!-- Project View -->
        <ProjectView
          v-else-if="currentProject"
          :key="currentProject.id"
          :project="currentProject"
          :allConversations="conversations"
          @close-project="closeProjectView"
          @new-chat-in-project="startNewChatInProject"
          @open-conversation="openConversationFromProject"
          @delete-conversation="clearConversation"
        />
        <!-- Normal Chat View -->
        <template v-else>
          <div :class="['dropdown-container', collapsed ? 'collapsed-dropdown' : ' ']">
            <ChatbotDropdown :chatbots="chatbots" :selectedChatbot="selectedChatbot" @select-chatbot="selectChatbot" />

            <!-- Info tooltip trigger -->
            <div class="info-tooltip-wrapper" ref="infoTooltipWrapper">
              <button
                class="info-btn"
                :class="{ active: showInfoTooltip }"
                :aria-expanded="showInfoTooltip"
                aria-haspopup="dialog"
                aria-label="About CamGPT — how to use this assistant"
                @click="toggleInfoTooltip"
                @keydown.escape="closeInfoTooltip"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
              </button>

              <transition name="info-tooltip">
                <div
                  v-if="showInfoTooltip"
                  class="info-tooltip-popover"
                  role="dialog"
                  aria-label="About CamGPT"
                  @keydown.escape="closeInfoTooltip"
                >
                  <div class="info-tooltip-header">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    <span>About {{ companyInformation.product_name || 'CamGPT' }}</span>
                  </div>
                  <p class="info-tooltip-body">
                    {{ companyInformation.product_name || 'CamGPT' }} is Cameron Sky's internal AI chat assistant, powered by Azure AI. Use it to draft content, summarize documents, answer questions, and more.
                  </p>
                  <ul class="info-tooltip-list">
                    <li>
                      <strong>Choose a chatbot</strong> — select a model from the dropdown to the left to switch between available assistants.
                    </li>
                    <li>
                      <strong>Ask anything</strong> — type your question or request in the chat input at the bottom and press Enter or the send button.
                    </li>
                    <li>
                      <strong>Upload files</strong> — attach images, PDFs, or Word documents using the + icon in the input bar (when supported by the selected chatbot).
                    </li>
                    <li>
                      <strong>Past conversations</strong> — access and resume previous chats from the sidebar on the left.
                    </li>
                    <li>
                      <strong>Projects</strong> — group related conversations into projects using the Projects section in the sidebar.
                    </li>
                  </ul>
                  <p class="info-tooltip-disclaimer">AI can make mistakes. Always verify important information.</p>
                </div>
              </transition>
            </div>
            <!-- End info tooltip -->

            <button
              v-if="(currentConversation && currentConversation.project_name) || pendingProjectName"
              class="project-indicator project-indicator--clickable"
              @click="openProjectFromIndicator"
              :title="`Back to ${currentConversation?.project_name || pendingProjectName}`"
              aria-label="Back to project"
            >
              <!-- Left chevron: communicates "back" navigation -->
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              <!-- Folder icon -->
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
              {{ currentConversation?.project_name || pendingProjectName }}
            </button>
          </div>
          <div v-if="!currentConversation" class="welcome-screen">
            <div class="welcome-inner">
              <ChatHeader
                :logo="logo" :title="selectedChatbot ? selectedChatbot.model_title : ''" :companyInformation="companyInformation" />
              <ChatMain
                :selectedChatbot="selectedChatbot" />
            </div>
          </div>
          <div v-else-if="currentConversation && currentMessages && currentMessages.length === 0 && !loadingMessages" class="header-container">
            <ChatHeader
              :logo="logo" :title="selectedChatbot ? selectedChatbot.model_title : ''" :companyInformation="companyInformation" />
          </div>
          <ChatConversation v-else :key="currentConversation.resource_id" :conversations="currentConversation" :messages="currentMessages" :companyInformation="companyInformation" :isThinking="isThinking" :selectedChatbot="selectedChatbot" :autoTtsOutput="autoTtsOutput" @submit-feedback="submitFeedback" />
          <!-- Stop generating button (visible during streaming) -->
          <div v-if="isStreaming" class="stop-generating-container">
            <button class="stop-generating-btn" @click="stopStreaming" title="Stop generating" aria-label="Stop generating">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="3" y="3" width="10" height="10" rx="1" />
              </svg>
              Stop generating
            </button>
          </div>
          <ChatFooter v-if="selectedChatbot && selectedChatbot.name" @send-message="sendMessage"
            @load-conversation="loadConversation" @auto-tts-changed="onAutoTtsChanged" :isThinking="isThinking" ref="chatFooter" :companyInformation="companyInformation" :collapsed="collapsed" :sidebarWidth="sidebarWidth" :selectedChatbot="selectedChatbot" />
        </template>
      </div>
      <!-- Artifacts toggle button (visible when a conversation is active) -->
      <button
        v-if="currentConversation && !showMemories && !showKnowledge"
        class="artifacts-toggle-btn"
        :class="{ active: showArtifactsPane }"
        @click="toggleConversationArtifacts"
        title="Toggle artifacts pane"
        aria-label="Toggle artifacts pane"
        :aria-expanded="showArtifactsPane"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      </button>
      <!-- Artifacts Slide-out Pane -->
      <ArtifactsPane
        :isOpen="showArtifactsPane"
        :conversationId="currentConversation ? currentConversation.resource_id : null"
        :projectId="artifactsPaneProjectId"
        :mode="artifactsPaneMode"
        @close="showArtifactsPane = false"
      />
    </div>
  </div>
</template>
 
<script>
import LoginScreen from './components/LoginScreen.vue';
import AppSidebar from './components/AppSidebar.vue';
import ChatHeader from './components/ChatHeader.vue';
import ChatMain from './components/ChatMain.vue';
import ChatConversation from './components/ChatConversation.vue';
import ChatFooter from './components/ChatFooter.vue';
import ChatbotDropdown from './components/ChatbotDropdown.vue';
import MemoryManagement from './components/MemoryManagement.vue';
import KnowledgeManagement from './components/KnowledgeManagement.vue'; // eslint-disable-line no-unused-vars
import ArtifactsPane from './components/ArtifactsPane.vue';
import ProjectView from './components/ProjectView.vue';
import axios from '@/axios-config';
import { initTheme, getCurrentTheme } from '@/utils/themeManager';
 
export default {
  components: {
    LoginScreen,
    AppSidebar,
    ChatHeader,
    ChatMain,
    ChatConversation,
    ChatFooter,
    ChatbotDropdown,
    MemoryManagement,
    KnowledgeManagement,
    ArtifactsPane,
    ProjectView
  },
 
  data() {
    return {
      currentConversation: null,
      currentMessages: [],
      conversations: [],
      chatbots: [],
      selectedChatbot: null,
      isNewConversation: true,
      isLoggedIn: false, // Track the login state
      newMessage: '',
      currentUser: null,
      thinkingConversationId: null,
      loadingMessages: false,
      isStreaming: false,
      streamController: null,  // AbortController for cancelling streams
      collapsed: false,
      sidebarWidth: parseInt(localStorage.getItem('sidebarWidth')) || 260,
      conversationMessagesMap: {},
      logo: null,      // reference to logo throughout site
      companyInformation: {},
      loading: true,
      error: null,
      showMemories: false,
      showKnowledge: false,
      showInfoTooltip: false,
      showArtifactsPane: false,
      artifactsPaneMode: 'conversation',
      artifactsPaneProjectId: null,
      pendingProjectId: null,
      pendingProjectName: null,
      autoTtsOutput: false,
      currentProject: null,
    };
  },
  mounted() {
    // Enable theme color transitions only after the first paint so the
    // browser does not animate from its default colors to the saved theme
    // on every page load (the "initial load flash" problem).
    // requestAnimationFrame defers to after the first frame is committed.
    requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-transitions-ready');
    });
  },

  created() {
    // Initialize theme immediately with default settings
    // This ensures the theme is set before any components render
    initTheme();

    this.checkAuthStatus();
    this.fetchCompanySettings();

    // DEBUG: log CSRF_TRUSTED_ORIGINS
    fetch('/api/debug/csrf-origins/').then(r => r.json()).then(d => console.log('CSRF_TRUSTED_ORIGINS:', d.CSRF_TRUSTED_ORIGINS)).catch(e => console.error('Failed to fetch CSRF origins:', e));
  },
 
  computed: {
    isThinking() {
      if (!this.thinkingConversationId) return false;
      // New conversation still being created — show thinking only while
      // we're still on the new-chat screen (no currentConversation yet)
      if (this.thinkingConversationId === '__pending__') return !this.currentConversation || this.isNewConversation;
      return this.thinkingConversationId === this.currentConversation?.resource_id;
    },
    appStyle() {
      const w = this.collapsed ? 60 : this.sidebarWidth;
      return {
        '--sidebar-current-width': w + 'px'
      };
    },
    filteredConversations() {
      return this.conversations.filter(convo => convo.chatbot === this.selectedChatbot);
    }
  },
  methods: {
    async checkAuthStatus() {
      try {
        const response = await axios.get('/api/auth/status', { withCredentials: true });

        if (response.data.isAuthenticated) {
          this.currentUser = response.data.user;
          this.isLoggedIn = true;
          console.log('User is logged in:', response.data.user);

          // Load user-specific data after confirming authentication
          this.loadChatbots();
          this.loadConversations();
          this.pickDefaultChatbot();
        } else {
          console.log('User not logged in');
          this.isLoggedIn = false;
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        this.isLoggedIn = false;
      }
    },

    async fetchCompanySettings() {
      this.loading = true;
      this.error = null;
      try {
        const companyInfoResponse = await axios.get('/api/company-setting/');
        this.companyInformation = companyInfoResponse.data;
        this.logo = this.companyInformation.logo || this.logo;

        if(getCurrentTheme().includes('dark'))
        {
          this.logo = this.companyInformation.logo_dark_mode;
        }

        // Initialize theme system with theme family from backend
        const themeFamily = this.companyInformation.theme_family;
        initTheme(themeFamily);

        // Update favicon dynamically
        this.updateHtmlHeader();
      } catch (err) {
        console.error(err);
        this.error = 'Failed to load company settings';
        // Initialize with default theme if fetch fails
        initTheme();
      } finally {
        this.loading = false;
      }
    },
    updateHtmlHeader() {
      const favicon = document.getElementById('favicon');
      const siteTitle = document.getElementById('siteTitle');
      if (favicon && this.companyInformation.favicon) {
        favicon.href = this.companyInformation.favicon;
      }
      if(siteTitle && this.companyInformation.product_name)
      {
        siteTitle.innerHTML = this.companyInformation.product_name;
      }
    },
    updateLogo(){
      if(getCurrentTheme().includes('dark'))
      {
        this.logo = this.companyInformation.logo_dark_mode; 
      }
      else
      {
        this.logo = this.companyInformation.logo;
      }
    },
    pickDefaultChatbot() {
      if (!this.chatbots.length || !this.currentUser) return;   // need both

      // First, check if there's a stored chatbot in localStorage
      const storedChatbot = localStorage.getItem('selectedChatbot');
      if (storedChatbot) {
        try {
          const chatbot = JSON.parse(storedChatbot);
          // Verify the chatbot still exists in the available chatbots
          const exists = this.chatbots.find(c => c.id === chatbot.id);
          if (exists) {
            this.selectedChatbot = exists;
            return; // Use the stored preference and exit early
          }
        } catch (error) {
          console.error('Error parsing stored chatbot:', error);
        }
      }

      // If no valid stored chatbot, use email-based logic
      const wantsFFGA = this.currentUser.email?.toLowerCase().endsWith('@ffga.com');

      // Look for bots by botid instead of hardcoded IDs
      let targetBotId;
      if (wantsFFGA) {
        targetBotId = 'ffga';
      } else {
        targetBotId = 'camgpt';
      }

      const bot = this.chatbots.find(b => b.botid === targetBotId);
      if (bot) {
        this.selectedChatbot = bot;
      } else {
        // Fallback to first available bot
        //this.selectedChatbot = this.chatbots[0];
      }
    },
    toggleInfoTooltip() {
      this.showInfoTooltip = !this.showInfoTooltip;
      if (this.showInfoTooltip) {
        // Register an outside-click listener AFTER this event finishes bubbling,
        // so the triggering click itself does not immediately close the popover.
        this.$nextTick(() => {
          document.addEventListener('click', this._closeInfoOnOutsideClick, true);
        });
      } else {
        document.removeEventListener('click', this._closeInfoOnOutsideClick, true);
      }
    },
    closeInfoTooltip() {
      if (!this.showInfoTooltip) return;
      this.showInfoTooltip = false;
      document.removeEventListener('click', this._closeInfoOnOutsideClick, true);
    },
    _closeInfoOnOutsideClick(event) {
      const wrapper = this.$refs.infoTooltipWrapper;
      // If the click was outside the wrapper entirely, close and clean up.
      // Clicks on the trigger button itself are handled by toggleInfoTooltip,
      // so we only act on clicks that are truly outside.
      if (wrapper && !wrapper.contains(event.target)) {
        this.showInfoTooltip = false;
        document.removeEventListener('click', this._closeInfoOnOutsideClick, true);
      }
    },
    handleSidebarCollapsed(isCollapsed) {
      this.collapsed = isCollapsed;
    },
    handleSidebarWidth(width) {
      this.sidebarWidth = width;
    },
    showMemoryScreen() {
      this.showMemories = true;
      this.showKnowledge = false;
    },
    showKnowledgeScreen() {
      this.showKnowledge = true;
      this.showMemories = false;
    },
    toggleConversationArtifacts() {
      if (this.showArtifactsPane && this.artifactsPaneMode === 'conversation') {
        this.showArtifactsPane = false;
      } else {
        this.artifactsPaneMode = 'conversation';
        this.artifactsPaneProjectId = null;
        this.showArtifactsPane = true;
      }
    },
    showProjectArtifacts(project) {
      this.artifactsPaneMode = 'project';
      this.artifactsPaneProjectId = project.id;
      this.showArtifactsPane = true;
    },
    showProjectView(project) {
      this.currentProject = project;
      this.currentConversation = null;
      this.currentMessages = [];
      this.showMemories = false;
      this.showKnowledge = false;
    },
    async openProjectFromIndicator() {
      const projectId = this.currentConversation?.project || this.pendingProjectId;
      const projectName = this.currentConversation?.project_name || this.pendingProjectName;
      if (!projectId) return;
      let project = { id: projectId, name: projectName };
      try {
        const response = await axios.get(`/api/projects/${projectId}/`);
        project = response.data;
      } catch (error) {
        console.error('Error fetching project from indicator:', error);
      }
      this.pendingProjectId = null;
      this.pendingProjectName = null;
      this.showProjectView(project);
    },
    closeProjectView() {
      this.currentProject = null;
    },
    openConversationFromProject(conversation) {
      this.currentProject = null;
      this.loadConversation(conversation);
    },


    async loadConversations() {
      try {
        const response = await axios.get('/api/conversation/', { withCredentials: true });
        this.conversations = response.data;
        this.pickDefaultChatbot();
      } catch (error) {
        console.error("Error loading conversations:", error);
      }
    },
 
    async loadChatbots() {
      try {
        const response = await axios.get('/api/assistant/');
        this.chatbots = response.data;
        this.pickDefaultChatbot();
      } catch (error) {
        console.error("Error loading chatbots:", error);
      }
    },
    resetToNewChat() {
      this.pickDefaultChatbot();
      this.currentConversation = null;
      this.isNewConversation = true;
      this.pendingProjectId = null;
      this.pendingProjectName = null;
      this.currentMessages = [];
      this.showMemories = false;
      this.showKnowledge = false;
      this.currentProject = null;
    },
    startNewChatInProject(project) {
      console.log('startNewChatInProject: project.id =', project.id, 'project.name =', project.name);
      this.pickDefaultChatbot();
      this.currentConversation = null;
      this.isNewConversation = true;
      this.pendingProjectId = project.id;
      this.pendingProjectName = project.name;
      this.currentMessages = [];
      // Close project view and overlays to show chat interface
      this.currentProject = null;
      this.showMemories = false;
      this.showKnowledge = false;
    },
 
    loadConversation(conversation) {
      console.log('loadConversation:', conversation.id, 'resource_id:', conversation.resource_id, 'project:', conversation.project);
      this.loadingMessages = true;
      this.currentMessages = [];
      this.currentConversation = conversation;
      this.isNewConversation = false;
      this.currentProject = null;
      this.showMemories = false;
      this.showKnowledge = false;
      // Clear any pending project state so the project indicator in the header
      // does not persist when navigating to a chat that is not in a project.
      this.pendingProjectId = null;
      this.pendingProjectName = null;

      // Find the matching chatbot by ID
      const chatbot = this.chatbots.find(bot => bot.id === conversation.ai_assistant);
      if (chatbot) this.selectedChatbot = chatbot;

      axios.get(`/api/conversation/${conversation.resource_id}/messages/`)
        .then(response => {
          this.currentMessages = Array.isArray(response.data) ? response.data : [];
          this.conversationMessagesMap = {
            ...this.conversationMessagesMap,
            [conversation.resource_id]: this.currentMessages,
          };
          console.log('loadConversation: loaded', this.currentMessages.length, 'messages');
        })
        .catch(error => {
          console.error("Error loading conversation messages:", error);
          if (!this.currentMessages) {
            this.currentMessages = [];
          }
        })
        .finally(() => {
          this.loadingMessages = false;
        });
    },
 
    async startNewChat(message, file, filePreview) {
      console.log('Attempting to send API request...');
      // Capture project ID before any async operations to avoid race conditions
      const projectId = this.pendingProjectId;
      const res = await axios.get('/api/get-csrf-token');
      const token = res.data.csrfToken;
      try {
        const requestData = {
            assistant: this.selectedChatbot,
            user_message: message
        };
        // Include project_id if starting a chat within a project
        if (projectId) {
            requestData.project_id = projectId;
            console.log('startNewChat: Including project_id =', projectId);
        }
        const response = await axios.post(
          '/api/newconvo/',
          requestData, {
          headers: {
            'X-CSRFToken': token
          }
        }
        );
        this.currentConversation = response.data;
        console.log('startNewChat: Response project =', response.data.project, 'project_name =', response.data.project_name);
        // Warn if project was requested but not set in response
        if (projectId && !response.data.project) {
          console.warn('startNewChat: project_id was sent but response has no project association!');
        }
        // Clear the pending project after conversation is created
        this.pendingProjectId = null;
        this.pendingProjectName = null;
        const userMsg = { role: 'user', content: message };
        if (file) {
          const isImage = file.type.startsWith('image/');
          userMsg.attachment = {
            url: filePreview || URL.createObjectURL(file),
            filename: file.name,
            type: isImage ? 'image' : 'document'
          };
        }
        this.currentMessages.push(userMsg);
        this.isNewConversation = false;
 
        return this.currentConversation
      } catch (error) {
        console.error("Error starting new conversation:", error);
      }
    },
 
 
    async clearConversation(conversationId) {
      const conversation = this.conversations.find(convo => convo.id === conversationId);
      this.conversations = this.conversations.filter(convo => convo.id !== conversationId);
      const url = `/api/clearconvo/${conversation.resource_id}/`;
      console.log('Attempting to send API request...');
      const res = await axios.get('/api/get-csrf-token');
      const token = res.data.csrfToken;
      axios.post(url, null, {
        headers: {
          'X-CSRFToken': token
        },
      });
      if (this.currentConversation && this.currentConversation.id === conversationId) {
        this.currentConversation = null;
        this.isNewConversation = true;
      }
      this.currentMessages = [];
    },
 
    async clearcurrentMessages() {
      this.currentMessages = []
    },
 
    async sendMessage(payload) {
      // payload is either a plain string or { message, file }
      const hasFile = typeof payload === 'object' && payload.file;
      const message = hasFile ? payload.message : payload;
      const file = hasFile ? payload.file : null;
      const filePreview = hasFile ? payload.preview : null;

      // show the thinking bubble for THIS conversation only
      this.thinkingConversationId = this.currentConversation?.resource_id || '__pending__';
      const res = await axios.get('/api/get-csrf-token');
      const token = res.data.csrfToken;
      if (!this.currentConversation || this.isNewConversation) {

        const conv = await this.startNewChat(message, file, filePreview);

        if (!conv) {
          console.error("Failed to create a new conversation.");
          this.thinkingConversationId = null;
          return;
        }
        this.currentConversation = conv;
        this.thinkingConversationId = conv.resource_id;

        if (!this.conversations.some(
          convo => convo.resource_id === this.currentConversation.resource_id
        )) {
          this.conversations.unshift(this.currentConversation);

        }
      } else {
        // Build user message with attachment preview for immediate display
        const userMsg = { role: 'user', content: message };
        if (file) {
          const isImage = file.type.startsWith('image/');
          userMsg.attachment = {
            url: filePreview || URL.createObjectURL(file),
            filename: file.name,
            type: isImage ? 'image' : 'document'
          };
        }
        this.currentMessages.push(userMsg);
        if (!this.currentMessages) {
          this.currentMessages = [];
        }


      }
      // Call the API for the AI response
      const conversationId = this.currentConversation.resource_id;


      const botId = this.selectedChatbot ? this.selectedChatbot.id : undefined;
      const botType = this.selectedChatbot ? this.selectedChatbot.botid : undefined;

      // Debug logging
      console.log('Selected chatbot:', this.selectedChatbot);
      console.log('Bot ID:', botId, 'Bot Type:', botType);

      // Detect if the selected chatbot is an image generation model
      const isImageModel = this.selectedChatbot && (
        (this.selectedChatbot.extra_config && this.selectedChatbot.extra_config.image_generation) ||
        ['dall-e', 'dalle', 'gpt-image', 'image'].some(kw =>
          (this.selectedChatbot.model_name || '').toLowerCase().includes(kw)
        )
      );

      // Use unified endpoint for file uploads and image generation models;
      // use streaming for text-only messages with non-image models
      if (file || isImageModel) {
        // File upload or image generation: use non-streaming unified endpoint
        const apiUrl = '/api/unified-assistant-response/';
        try {
          const formData = new FormData();
          formData.append('conversation_id', conversationId);
          formData.append('botid', botType);
          formData.append('user_message', message || '');
          if (file) {
            formData.append('file', file);
            console.log('[CamGPT] Uploading file:', file.name, 'size:', file.size, 'type:', file.type);
          }
          const response = await axios.post(apiUrl, formData, {
            headers: {
              'X-CSRFToken': token,
              'Content-Type': 'multipart/form-data',
            }
          });
          this.thinkingConversationId = null;

          // Log debug info to browser console for troubleshooting file uploads
          if (response.data._file_debug) {
            console.log('[CamGPT] File upload debug:', response.data._file_debug);
          }
          if (response.data._debug) {
            console.log('[CamGPT] Provider debug:', response.data._debug);
          }

          this.currentMessages.push({
            role: 'assistant',
            html_content: response.data.reply,
            resource_id: response.data.message_resource_id,
            feedback_vote: null,
          });
          this.$refs.chatFooter.enableInput();
        } catch (error) {
          console.error("Error fetching AI response:", error);
          this.thinkingConversationId = null;

          const ctx = isImageModel ? 'image' : 'response';
          const { title, description } = this.classifyError(error, ctx);
          const data = error.response?.data || {};
          const userMessage = this.buildErrorHtml(title, description, {
            detail: data.error || data.detail || error.message,
            errorCode: data.error_code,
            timestamp: data.timestamp || new Date().toISOString(),
            status: error.response?.status,
          });

          this.currentMessages.push({
            role: 'assistant',
            html_content: userMessage,
            resource_id: data.message_resource_id || null,
          });
        } finally {
          if (this.$refs.chatFooter && this.$refs.chatFooter.enableInput) {
            this.$refs.chatFooter.enableInput();
          }
        }
      } else if (this.selectedChatbot && this.selectedChatbot.supports_stream === false) {
        // Model does not support streaming: fall back to non-streaming unified endpoint
        const apiUrl = '/api/unified-assistant-response/';
        try {
          const formData = new FormData();
          formData.append('conversation_id', conversationId);
          formData.append('botid', botType);
          formData.append('user_message', message || '');
          const response = await axios.post(apiUrl, formData, {
            headers: {
              'X-CSRFToken': token,
              'Content-Type': 'multipart/form-data',
            }
          });
          this.thinkingConversationId = null;
          this.currentMessages.push({
            role: 'assistant',
            html_content: response.data.reply,
            resource_id: response.data.message_resource_id,
            feedback_vote: null,
          });
          this.$refs.chatFooter.enableInput();
        } catch (error) {
          console.error("Error fetching AI response (non-streaming fallback):", error);
          this.thinkingConversationId = null;

          const { title, description } = this.classifyError(error);
          const data = error.response?.data || {};
          const userMessage = this.buildErrorHtml(title, description, {
            detail: data.error || data.detail || error.message,
            errorCode: data.error_code,
            timestamp: data.timestamp || new Date().toISOString(),
            status: error.response?.status,
          });

          this.currentMessages.push({
            role: 'assistant',
            html_content: userMessage,
            resource_id: data.message_resource_id || null,
          });
        } finally {
          if (this.$refs.chatFooter && this.$refs.chatFooter.enableInput) {
            this.$refs.chatFooter.enableInput();
          }
        }
      } else {
        // Text-only with streaming-capable model: use SSE streaming endpoint
        await this.sendMessageStreaming(conversationId, botType, message, token);
      }
  },

    async sendMessageStreaming(conversationId, botType, message, csrfToken) {
      // Push a placeholder assistant message for streaming
      const streamingMsg = {
        role: 'assistant',
        content: '',
        html_content: '',
        streaming: true,
        resource_id: null,
        feedback_vote: null,
      };
      this.currentMessages.push(streamingMsg);
      const msgIndex = this.currentMessages.length - 1;

      this.thinkingConversationId = null;
      this.isStreaming = true;

      // Create an AbortController so the user can cancel
      this.streamController = new AbortController();
      let fullText = '';
      let displayedLen = 0;
      let smoothInterval = null;

      // Smooth streaming: reveal text at an adaptive rate via setInterval.
      // When data arrives in network bursts the animation spreads the reveal across
      // frames so the user sees a steady typing effect instead of sudden jumps.
      // Uses setInterval (not requestAnimationFrame) because rAF pauses when the
      // tab loses focus and can be unreliable behind reverse proxies.
      smoothInterval = setInterval(() => {
        const buffered = fullText.length - displayedLen;
        if (buffered > 0) {
          // Adaptive step: small buffer → slow reveal, large buffer → fast catch-up
          const step = Math.max(1, Math.ceil(buffered / 8));
          displayedLen = Math.min(displayedLen + step, fullText.length);
          if (this.currentMessages[msgIndex]) {
            this.currentMessages[msgIndex].content = fullText.substring(0, displayedLen);
          }
        }
      }, 16);

      try {
        const response = await fetch('/api/stream/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            botid: botType,
            user_message: message,
            conversation_id: conversationId,
          }),
          signal: this.streamController.signal,
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Stream error (${response.status}): ${text}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        fullText = '';

        let reading = true;
        while (reading) {
          const { done, value } = await reader.read();
          if (done) { reading = false; break; }

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line in buffer

          let currentEventType = null;
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.substring(6);

              if (currentEventType === 'token') {
                // Unescape SSE-encoded newlines
                const token = data.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
                fullText += token;
                // Animation loop handles smooth text reveal
                currentEventType = null;
              } else if (currentEventType === 'done') {
                if (smoothInterval) { clearInterval(smoothInterval); smoothInterval = null; }
                try {
                  const doneData = JSON.parse(data);
                  // Replace streaming content with final HTML
                  this.currentMessages[msgIndex].html_content = doneData.html_content;
                  this.currentMessages[msgIndex].content = fullText;
                  this.currentMessages[msgIndex].resource_id = doneData.message_resource_id;
                  this.currentMessages[msgIndex].streaming = false;
                } catch (e) {
                  console.error('Error parsing done event:', e);
                }
                currentEventType = null;
              } else if (currentEventType === 'status') {
                // Status update (e.g., "Generating documents...")
                this.currentMessages[msgIndex].statusMessage = data;
                currentEventType = null;
              } else if (currentEventType === 'error') {
                if (smoothInterval) { clearInterval(smoothInterval); smoothInterval = null; }
                console.error('Stream error event:', data);

                // Parse structured error JSON from backend (falls back to plain string)
                let errDetail = data;
                let errCode = '';
                let errTimestamp = new Date().toISOString();
                let errMsgResourceId = null;
                try {
                  const parsed = JSON.parse(data);
                  errDetail = parsed.detail || data;
                  errCode = parsed.error_code || '';
                  errTimestamp = parsed.timestamp || errTimestamp;
                  errMsgResourceId = parsed.message_resource_id || null;
                } catch (_) { /* plain text fallback */ }

                const partialText = fullText ? `<div class="markdown-body" style="margin-bottom:10px;">${fullText}</div>` : '';
                const { title, description } = this.classifyError(errDetail, 'stream');
                const errorHtml = this.buildErrorHtml(title, description, {
                  detail: errDetail,
                  errorCode: errCode,
                  timestamp: errTimestamp,
                }, partialText);
                this.currentMessages[msgIndex].html_content = errorHtml;
                this.currentMessages[msgIndex].content = fullText || '';
                this.currentMessages[msgIndex].streaming = false;
                this.currentMessages[msgIndex].resource_id = errMsgResourceId;
                currentEventType = null;
              }
            }
          }
        }
      } catch (error) {
        if (smoothInterval) { clearInterval(smoothInterval); smoothInterval = null; }
        if (error.name === 'AbortError') {
          console.log('Stream was cancelled by user');
          this.currentMessages[msgIndex].streaming = false;
          if (!this.currentMessages[msgIndex].content) {
            this.currentMessages[msgIndex].content = '(Generation stopped)';
          }
        } else {
          console.error('Streaming error:', error);
          const partialText = fullText ? `<div class="markdown-body" style="margin-bottom:10px;">${fullText}</div>` : '';
          const { title, description } = this.classifyError(error, 'stream');
          const errorHtml = this.buildErrorHtml(title, description, {
            detail: error.message || 'Connection lost',
            timestamp: new Date().toISOString(),
          }, partialText);
          this.currentMessages[msgIndex].html_content = errorHtml;
          this.currentMessages[msgIndex].content = fullText || '';
          this.currentMessages[msgIndex].streaming = false;
        }
      } finally {
        if (smoothInterval) clearInterval(smoothInterval);
        this.isStreaming = false;
        this.streamController = null;
        if (this.$refs.chatFooter && this.$refs.chatFooter.enableInput) {
          this.$refs.chatFooter.enableInput();
        }
      }
    },

    stopStreaming() {
      if (this.streamController) {
        this.streamController.abort();
      }
    },
 
    /**
     * Build a user-friendly inline error message with a collapsible
     * "Show details" section that developers can use to diagnose the issue.
     *
     * @param {string} title       - Short friendly title (e.g. "Something went wrong")
     * @param {string} description - One-line explanation for the user
     * @param {Object} details     - Technical info: { detail, errorCode, timestamp, status }
     * @param {string} [partialHtml] - Any partial streamed content to preserve above the error
     */
    buildErrorHtml(title, description, details = {}, partialHtml = '') {
      const prefix = partialHtml || '';

      // Escape HTML in detail strings to prevent injection
      const esc = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

      const rows = [];
      if (details.errorCode) rows.push(`<span class="error-detail-label">Code:</span> ${esc(details.errorCode)}`);
      if (details.detail)    rows.push(`<span class="error-detail-label">Detail:</span> ${esc(details.detail)}`);
      if (details.timestamp) rows.push(`<span class="error-detail-label">Time:</span> ${esc(details.timestamp)}`);
      if (details.status)    rows.push(`<span class="error-detail-label">HTTP:</span> ${esc(details.status)}`);

      const detailsBlock = rows.length
        ? `<details class="error-details"><summary class="error-details-toggle">Show technical details</summary><div class="error-details-content">${rows.map(r => `<p>${r}</p>`).join('')}</div></details>`
        : '';

      return `${prefix}<div class="inline-error-message"><svg class="error-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div class="error-body"><p class="error-title">${esc(title)}</p><p class="error-description">${esc(description)}</p>${detailsBlock}</div></div>`;
    },

    /**
     * Classify an error into a user-friendly title + description.
     * Works for both axios errors and raw detail strings.
     */
    classifyError(error, context = 'response') {
      const msg = (typeof error === 'string' ? error : (error?.message || '')).toLowerCase();
      const httpStatus = error?.response?.status;
      const errorCode = error?.response?.data?.error_code || '';

      if (httpStatus === 401 || errorCode === 'AUTH_ERROR')
        return { title: 'Session expired', description: 'Your session has expired. Please refresh the page and log in again.' };
      if (httpStatus === 429 || msg.includes('rate limit') || msg.includes('429'))
        return { title: 'Too many requests', description: 'The AI service is currently busy. Please wait a moment and try again.' };
      if (httpStatus === 404 || errorCode === 'NOT_FOUND')
        return { title: 'Not found', description: 'The requested resource could not be found. It may have been deleted.' };
      if (msg.includes('timeout') || msg.includes('timed out') || msg.includes('deadline'))
        return { title: 'Response timed out', description: 'The AI model took too long to respond. This may be due to high demand — please try again.' };
      if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch') || msg.includes('connection') || msg.includes('err_connection'))
        return { title: 'Connection error', description: 'We lost the connection while waiting for a response. Please check your network and try again.' };
      if (context === 'image')
        return { title: 'Image generation failed', description: 'The image could not be generated. Please try again or rephrase your prompt.' };
      if (context === 'stream')
        return { title: 'Streaming interrupted', description: 'The response was interrupted before it could finish. Please try sending your message again.' };
      return { title: 'Something went wrong', description: 'An unexpected error occurred while processing your request. Please try again.' };
    },

    selectChatbot(bot) {
      // Skip reset if the same chatbot is already selected.
      // ChatbotDropdown emits select-chatbot on mount (immediate watcher),
      // which would otherwise wipe currentConversation when navigating
      // back from ProjectView.
      if (this.selectedChatbot && this.selectedChatbot.id === bot.id) {
        return;
      }
      this.selectedChatbot = bot;
      // Preserve pending project state when switching chatbots
      // (user may switch chatbot before sending first message in a project chat)
      const preserveProject = this.pendingProjectId;
      const preserveProjectName = this.pendingProjectName;
      this.currentConversation = null;
      this.isNewConversation = true;
      this.currentMessages = [];
      if (preserveProject) {
        this.pendingProjectId = preserveProject;
        this.pendingProjectName = preserveProjectName;
      }
    },

    onAutoTtsChanged(value) {
      this.autoTtsOutput = value;
    },

    async submitFeedback(messageResourceId, vote) {
      try {
        const res = await axios.get('/api/get-csrf-token');
        const token = res.data.csrfToken;
        const response = await axios.post(
          `/api/message/${messageResourceId}/feedback/`,
          { vote },
          { headers: { 'X-CSRFToken': token } }
        );
        // Update local message state
        const msg = this.currentMessages.find(m => m.resource_id === messageResourceId);
        if (msg) {
          msg.feedback_vote = response.data.feedback_vote;
        }
      } catch (error) {
        console.error('Error submitting feedback:', error);
      }
    },

    handleLogin() {
      this.isLoggedIn = true; // Set login state to true
      window.location.href = '/accounts/microsoft/login/';
    },
    handleLogout() {
      window.location.href = "/logout/";
      this.isLoggedIn = false; // Set login state to false
    },
  },
 
};
</script>
 
<style scoped>
html,
body{
  margin: 0;
  padding: 0;
  /* overflow: hidden; */
}
 
.app {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

.main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100% - var(--sidebar-current-width, var(--sidebar-width)));
  height: 100%;
  min-width: 0;
  overflow: hidden;
  flex-grow: 1;
  padding-top: var(--topbar-height);
}

.main-content--project {
  align-items: stretch;
  padding-top: 0;
}


.dropdown-container {
  position: fixed;
  top: 0;
  left: var(--sidebar-current-width, var(--sidebar-width));
  right: 0;
  height: var(--topbar-height);
  display: flex;
  align-items: center;
  padding: 0 var(--space-lg);
  gap: var(--space-sm);
  background: var(--bg-topbar);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-subtle);
  z-index: 10;
}

.project-indicator {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-xs) var(--space-md);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  color: var(--text-tertiary);
  background: var(--accent-muted);
  border-radius: var(--radius-pill);
  white-space: nowrap;
  border: none;
  font-family: inherit;
}

.project-indicator--clickable {
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.project-indicator--clickable:hover {
  background: var(--accent-primary);
  color: var(--text-on-accent, #fff);
}

.project-indicator--clickable:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

/* collapsed-dropdown no longer needed - dynamic var handles it */

/* Stop generating button */
.stop-generating-container {
  display: flex;
  justify-content: center;
  padding: var(--space-sm) 0;
  width: 100%;
}

.stop-generating-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-lg);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  color: var(--text-tertiary);
  font-size: var(--text-sm);
  font-family: var(--font-sans);
  cursor: pointer;
  transition: var(--transition-colors), var(--transition-shadow);
  box-shadow: var(--shadow-card);
}

.stop-generating-btn:hover {
  background: var(--bg-surface-hover);
  border-color: var(--border-primary);
  color: var(--text-primary);
  box-shadow: var(--shadow-card-hover);
}

.stop-generating-btn svg {
  flex-shrink: 0;
}

.welcome-screen {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow-y: auto;
  padding-bottom: 100px; /* clear the fixed footer */
  box-sizing: border-box;
  animation: view-enter 0.32s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@keyframes view-enter {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.welcome-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2xl);
  width: 100%;
  padding: var(--space-xl);
  box-sizing: border-box;
}

.header-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: var(--space-xl);
  box-sizing: border-box;
  animation: view-enter 0.32s cubic-bezier(0.4, 0, 0.2, 1) both;
  font-family: var(--font-display);
}

/* Artifacts toggle button */
.artifacts-toggle-btn {
  position: fixed;
  top: calc((var(--topbar-height) - 36px) / 2);
  right: var(--space-lg);
  z-index: 50;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-sm);
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-colors), var(--transition-shadow);
  box-shadow: var(--shadow-sm);
}

.artifacts-toggle-btn:hover {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
  border-color: var(--border-primary);
}

.artifacts-toggle-btn.active {
  background: var(--accent-muted);
  color: var(--accent-primary);
  border-color: var(--accent-primary);
}

/* ===== Info tooltip ===== */

.info-tooltip-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

/* Circular trigger button */
.info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: var(--transition-colors), var(--transition-shadow);
}

.info-btn:hover {
  background: var(--bg-surface-hover);
  border-color: var(--border-primary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.info-btn:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

.info-btn.active {
  background: var(--accent-muted);
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

/* Popover card */
.info-tooltip-popover {
  position: absolute;
  top: calc(100% + var(--space-sm));
  left: 0;
  z-index: 200;
  width: 320px;
  max-width: calc(100vw - var(--space-xl) * 2);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
  padding: var(--space-lg);
  font-family: var(--font-sans);
  /* Prevent viewport overflow on the right edge */
  max-height: calc(100vh - var(--topbar-height) - var(--space-xl));
  overflow-y: auto;
}

/* Header row: icon + title */
.info-tooltip-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
  color: var(--accent-primary);
}

.info-tooltip-header span {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  letter-spacing: var(--tracking-tight);
}

/* Body paragraph */
.info-tooltip-body {
  margin: 0 0 var(--space-md) 0;
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  line-height: var(--leading-relaxed);
}

/* Tips list */
.info-tooltip-list {
  margin: 0 0 var(--space-md) 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.info-tooltip-list li {
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  line-height: var(--leading-normal);
  padding-left: var(--space-md);
  position: relative;
}

.info-tooltip-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 7px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--accent-primary);
  opacity: 0.7;
}

.info-tooltip-list li strong {
  font-weight: var(--font-medium);
  color: var(--text-primary);
}

/* Disclaimer line */
.info-tooltip-disclaimer {
  margin: 0;
  padding-top: var(--space-md);
  border-top: 1px solid var(--border-subtle);
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

/* Enter/leave transition for the info tooltip popover.
 * Uses CSS property transitions instead of animation+reverse so that
 * Vue's v-if teardown waits for the transition to fully finish before
 * removing the element — eliminating the scale/opacity snap (jitter)
 * that occurred when animation-fill-mode was not honoured on leave. */
.info-tooltip-enter-active,
.info-tooltip-leave-active {
  transition: opacity var(--duration-base) var(--ease-out),
              transform var(--duration-base) var(--ease-out);
}

.info-tooltip-enter-from,
.info-tooltip-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.info-tooltip-enter-to,
.info-tooltip-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
 